import { Currency } from '@form-factory/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import type { TRPCClientErrorLike } from '@trpc/client';
import { Button, Input, toast } from '@utima/ui';
import { useState } from 'react';

import { trpc } from '@/services/trpc';

interface BalanceTopUpProps {
  organizationId: string;
  onBalanceAdded: () => void;
}

export function BalanceTopUp({
  organizationId,
  // onBalanceAdded, // Currently unused after redirect
}: BalanceTopUpProps) {
  const { t } = useLingui();

  const [organizationData] = trpc.organizations.findOne.useSuspenseQuery({
    id: organizationId,
  });
  const orgCurrency = organizationData.currency ?? Currency.USD;

  const minAmount = orgCurrency === Currency.CZK ? 100 : 5;
  const currencySymbol = orgCurrency === Currency.CZK ? 'Kč' : '$';

  const [fundAmount, setFundAmount] = useState<number>(minAmount * 2);
  const [isLoading, setIsLoading] = useState(false);

  const topUpMutation = trpc.subscriptions.topUpBalance.useMutation({
    onSuccess: (data: { url?: string }) => {
      setIsLoading(false);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned from server');
        toast.error(t`Error`, {
          description: t`No checkout URL returned from server`,
        });
      }
    },
    onError: (error: TRPCClientErrorLike<any>) => {
      console.error('Error creating balance top-up checkout:', error);
      setIsLoading(false);
      toast.error(t`Error`, {
        description: error.message,
      });
    },
  });

  const handleTopUp = () => {
    if (fundAmount < minAmount) {
      toast.error(t`Error`, {
        description: t`Minimum amount to top up is ${minAmount}${currencySymbol}`,
      });

      return;
    }

    setIsLoading(true);

    const baseUrl = window.location.origin;
    const currentPath = window.location.pathname;
    const successUrl = `${baseUrl}${currentPath}?topup=success`;
    const cancelUrl = `${baseUrl}${currentPath}?topup=cancelled`;

    topUpMutation.mutate({
      organizationId,
      amount: fundAmount,
      successUrl,
      cancelUrl,
    });
  };

  return (
    <div className='space-y-4'>
      <p className='text-sm text-gray-500'>
        <Trans>
          Purchase additional funds to extend your chatbot usage beyond your
          subscription limit.
        </Trans>
      </p>

      <div className='flex flex-col gap-4'>
        <div>
          <label
            htmlFor='fundAmount'
            className='mb-1 block text-sm font-medium'
          >
            <Trans>Amount to Purchase ({currencySymbol})</Trans>
          </label>
          <div className='flex items-center gap-2'>
            <Input
              id='fundAmount'
              type='number'
              min={minAmount}
              value={fundAmount}
              onChange={e => setFundAmount(Number(e.target.value))}
              className='w-32'
            />
            <Button
              data-cta='admin-balance-top-up-proceed-button'
              onClick={handleTopUp}
              disabled={isLoading || fundAmount < minAmount}
              loading={isLoading}
            >
              <Trans>Proceed to Checkout</Trans>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
